import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createArticle,
  deleteArticleCoverImage,
  getArticle,
  updateArticle,
  uploadArticleCoverImage,
} from "../articlesApi";
import { Article } from "../types";
import { FieldSection, SegmentedField, SelectField, TextField } from "../components/FormField";
import RichTextEditor from "../components/RichTextEditor";

const emptyArticle: Article = {
  id: "",
  slug: "",
  status: "draft",
  type: "article",
  title: "",
  excerpt: "",
  coverImageUrl: null,
  bodyHtml: "",
  externalUrl: "",
  createdAt: "",
  updatedAt: "",
};

export default function AdminArticleEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === "new";
  const navigate = useNavigate();

  const [article, setArticle] = useState<Article>(emptyArticle);
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isNew || !id) return;
    getArticle(id)
      .then((a) => {
        setArticle(a);
        setSlug(a.slug);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "加载失败"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function set<K extends keyof Article>(key: K, value: Article[K]) {
    setArticle((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { status, type, title, excerpt, bodyHtml, externalUrl } = article;
      if (isNew) {
        const created = await createArticle(slug, { status, type, title, excerpt, bodyHtml, externalUrl });
        navigate(`/admin/articles/${created.id}`, { replace: true });
      } else if (id) {
        const updated = await updateArticle(id, {
          slug,
          status,
          type,
          title,
          excerpt,
          bodyHtml,
          externalUrl,
        });
        setArticle(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function handleCoverSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !id || isNew) return;
    setUploading(true);
    setError(null);
    try {
      setArticle(await uploadArticleCoverImage(id, file));
    } catch (err) {
      setError(err instanceof Error ? err.message : "封面图上传失败");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleRemoveCover() {
    if (!id || isNew) return;
    setArticle(await deleteArticleCoverImage(id));
  }

  if (loading) return <p className="text-secondary-light">加载中…</p>;

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-secondary-dark">
          {isNew ? "新建文章" : `编辑：${article.title || article.slug}`}
        </h1>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/admin/articles")}
            className="text-secondary-light hover:text-secondary-dark text-sm"
          >
            返回列表
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-gold hover:bg-gold-light disabled:opacity-60 text-secondary-dark font-medium rounded-full px-6 py-2 text-sm transition-colors"
          >
            {saving ? "保存中…" : "保存"}
          </button>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <FieldSection title="基本信息">
        <SegmentedField
          label="类型"
          value={article.type}
          onChange={(v) => set("type", v)}
          options={[
            { value: "article", label: "文章（站内正文）" },
            { value: "external_link", label: "外部链接" },
          ]}
        />
        <div className="grid grid-cols-2 gap-x-6">
          <TextField
            label="Slug（网址标识，如 why-invest-in-malta）"
            value={slug}
            required
            onChange={setSlug}
          />
          <SelectField
            label="状态"
            value={article.status}
            onChange={(v) => set("status", v)}
            options={[
              { value: "draft", label: "草稿（不在官网显示）" },
              { value: "published", label: "已发布" },
            ]}
          />
        </div>
        <TextField label="标题" value={article.title} required onChange={(v) => set("title", v)} />
        <TextField
          label="摘要（列表页展示，可选）"
          value={article.excerpt ?? ""}
          onChange={(v) => set("excerpt", v)}
        />
        {article.type === "external_link" && (
          <TextField
            label="外部链接地址（点击卡片将跳转到此网址）"
            value={article.externalUrl ?? ""}
            placeholder="https://…"
            onChange={(v) => set("externalUrl", v)}
          />
        )}
      </FieldSection>

      <FieldSection title="封面图">
        {isNew ? (
          <p className="text-sm text-secondary-light">请先保存文章，再上传封面图。</p>
        ) : (
          <div>
            {article.coverImageUrl && (
              <div className="relative w-64 mb-3 group">
                <img src={article.coverImageUrl} alt="封面" className="w-full aspect-video object-cover rounded-lg border border-fourth-light" />
                <button
                  type="button"
                  onClick={handleRemoveCover}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-secondary-dark/80 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  title="删除"
                >
                  ✕
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleCoverSelected}
              disabled={uploading}
              className="block text-xs"
            />
            {uploading && <span className="text-xs text-secondary-light">上传中…</span>}
          </div>
        )}
      </FieldSection>

      {article.type === "article" && (
        <FieldSection title="正文">
          <RichTextEditor content={article.bodyHtml ?? ""} onChange={(html) => set("bodyHtml", html)} />
        </FieldSection>
      )}
    </form>
  );
}
